'use client';

import { createContext, useContext, useMemo } from 'react';
import { type Locale, type PluralForms, plural as pluralise, type Translations } from '@/lib/i18n';

/**
 * Carries the dictionary from the server render into client components.
 *
 * The alternative was threading `t` down as a prop, which for the board would
 * mean passing it through the kanban into every column, card and dialog — four
 * levels of plumbing that exists only to move a constant.
 *
 * The dictionary is plain JSON, so it crosses the server/client boundary
 * without any serialisation work, and the server has already chosen the
 * language by the time this mounts. There is no loading state and no flash of
 * English: the first paint is already translated.
 */

export type I18n = {
  t: Translations;
  locale: Locale;
  /** Count-aware lookup; see `plural` in lib/i18n for why this is not `n === 1`. */
  p: (forms: PluralForms, count: number, values?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({
  locale,
  dictionary,
  children,
}: {
  locale: Locale;
  dictionary: Translations;
  children: React.ReactNode;
}) {
  const value = useMemo<I18n>(
    () => ({
      t: dictionary,
      locale,
      p: (forms, count, values) => pluralise(locale, forms, count, values),
    }),
    [locale, dictionary]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Throws rather than falling back to English when the provider is missing.
 *
 * A silent fallback would mean a screen that renders correctly in development
 * and ships half-translated, which is precisely the failure this whole setup
 * exists to prevent.
 */
export function useI18n(): I18n {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used inside <I18nProvider>');
  }
  return context;
}
